import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Logger } from '@nsalaun/ng-logger';
import { Observable, forkJoin, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { UserContext } from '@mysvg/api/auth/models/user-context';
import { AuthControllerService } from '@mysvg/api/auth/services/auth-controller.service';

import { ErrorMessageKey } from '@svg-frontends/error/enums/error-message-key';

import { SvgContext } from '../../context/models/svg-context.model';
import { KeycloakTokenClaims } from '../../context/models/user-token-claims.model';
import { AuthenticationService } from './authentication.service';
import { OauthWrapperService } from './oauth-wrapper.service';

@Injectable({ providedIn: 'root' })
export class SvgAuthenticationService extends AuthenticationService<SvgContext> {
	private authControllerService = inject(AuthControllerService);
	private oauthWrapperService = inject<OauthWrapperService<KeycloakTokenClaims>>(OauthWrapperService);
	private logger = inject(Logger);

	notLoggedIn(): SvgContext {
		return SvgContext.notLoggedIn();
	}

	postLoginFlow(contextId: string | null): Observable<SvgContext> {
		this.logger.debug(`[SvgAuthenticationService] says found groupId / contextId ${contextId}`);

		return forkJoin([
			this.oauthWrapperService.getDecodedToken(),
			contextId == null
				? this.authControllerService.getUserContext()
				: this.authControllerService.getUserContextByGroupId({ groupId: contextId }),
			this.getAndIgnoreErrorInterceptor(contextId),
		]).pipe(
			map(([userTokenClaims, user, activities]: [KeycloakTokenClaims, UserContext, string[]]) =>
				SvgContext.from(userTokenClaims, user, activities),
			),
		);
	}

	/**
	 * 401 (unauthorized) errors from activities should not result in an 401 error
	 * if so, login would say wrong credentials
	 * mark 401 errors on this as internal (500) error
	 */
	private getAndIgnoreErrorInterceptor(contextId: string | null): Observable<string[]> {
		let activitiesControllerCall: Observable<string[]>;
		if (contextId != null) {
			activitiesControllerCall = this.authControllerService.getUsersActivitiesForGroup({ groupId: contextId });
		} else {
			activitiesControllerCall = this.authControllerService.getUsersActivities();
		}

		return activitiesControllerCall.pipe(
			catchError((error: HttpErrorResponse) =>
				throwError(
					new HttpErrorResponse({
						...error,
						error: new Error(ErrorMessageKey.AUTH_NO_ACTIVITIES),
						status: error.status === 401 ? 500 : error.status,
					}),
				),
			),
		);
	}
}
